import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  Users, Mail, TrendingUp, Upload, Send, Plus, Search, Trash2, RefreshCw,
  ChevronLeft, Eye, AlertCircle, CheckCircle, Clock,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

const STATE_COLORS = {
  "Nuovo": "bg-blue-100 text-blue-800",
  "Email Inviata": "bg-amber-100 text-amber-800",
  "Aperta": "bg-purple-100 text-purple-800",
  "Interessato": "bg-emerald-100 text-emerald-800",
  "Non Interessato": "bg-gray-100 text-gray-700",
  "Da Richiamare": "bg-orange-100 text-orange-800",
  "Errore": "bg-red-100 text-red-800",
};

const STATES = ["Nuovo", "Email Inviata", "Aperta", "Interessato", "Non Interessato", "Da Richiamare", "Errore"];

const LeadFinderPage = () => {
  const token = localStorage.getItem("token");
  const H = { headers: { Authorization: `Bearer ${token}` } };

  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ regione: "", stato: "", comune: "" });
  const [showAdd, setShowAdd] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");
  const [newLead, setNewLead] = useState({ ragione_sociale: "", email: "", telefono: "", comune: "", regione: "", note: "" });
  const [csvFile, setCsvFile] = useState(null);
  const [sending, setSending] = useState(false);

  const loadStats = async () => {
    try {
      const r = await axios.get(`${API}/leadfinder/stats`, H);
      setStats(r.data);
    } catch (e) { /* silent */ }
  };

  const loadLeads = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => v && params.append(k, v));
      params.append("limit", "500");
      const r = await axios.get(`${API}/leadfinder/centri?${params}`, H);
      setLeads(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch (e) {
      toast.error("Errore caricamento lead");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadStats(); loadLeads(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { loadLeads(); /* eslint-disable-next-line */ }, [filters]);

  const createLead = async () => {
    if (!newLead.ragione_sociale || !newLead.email) {
      toast.error("Nome centro ed email obbligatori");
      return;
    }
    try {
      await axios.post(`${API}/leadfinder/centri`, newLead, H);
      toast.success("Lead aggiunto");
      setShowAdd(false);
      setNewLead({ ragione_sociale: "", email: "", telefono: "", comune: "", regione: "", note: "" });
      loadLeads(); loadStats();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore creazione");
    }
  };

  const deleteLead = async (id) => {
    if (!window.confirm("Eliminare il lead?")) return;
    try {
      await axios.delete(`${API}/leadfinder/centri/${id}`, H);
      toast.success("Eliminato");
      loadLeads(); loadStats();
    } catch { toast.error("Errore"); }
  };

  const uploadCsv = async () => {
    if (!csvFile) { toast.error("Seleziona un file CSV"); return; }
    const fd = new FormData();
    fd.append("file", csvFile);
    try {
      const r = await axios.post(`${API}/leadfinder/centri/import-csv`, fd, H);
      toast.success(`Importati ${r.data.imported} · Scartati ${r.data.skipped}`);
      setCsvFile(null);
      loadLeads(); loadStats();
    } catch (e) {
      toast.error("Errore import CSV");
    }
  };

  const sendBatch = async (leadIds = null, testMode = false) => {
    if (!window.confirm(testMode
      ? `TEST MODE: nessuna email reale, solo log.\nProcedere?`
      : `Inviare email a ${leadIds ? leadIds.length : "TUTTI i lead NUOVO"}?\nMax 80/giorno.`)) return;
    setSending(true);
    try {
      const payload = { max_count: 80, test_mode: testMode };
      if (leadIds) payload.lead_ids = leadIds;
      const r = await axios.post(`${API}/leadfinder/centri/send-batch`, payload, H);
      toast.success(`Inviate: ${r.data.sent} · Errori: ${r.data.failed}${r.data.test_mode ? " (TEST)" : ""}`);
      loadLeads(); loadStats();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore invio");
    } finally { setSending(false); }
  };

  const previewEmail = async () => {
    try {
      const r = await axios.get(`${API}/leadfinder/preview-email?nome_centro=Centro Estetico Bella Vita&comune=Milano&regione=Lombardia`);
      setPreviewHtml(r.data.html);
      setShowPreview(true);
    } catch { toast.error("Errore preview"); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 to-white p-6" data-testid="leadfinder-page">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <Link to="/admin" className="text-xs text-stone-500 hover:text-stone-800 flex items-center gap-1 mb-1">
              <ChevronLeft className="w-3 h-3" /> Dashboard Admin
            </Link>
            <h1 className="text-3xl font-light text-stone-800" style={{ fontFamily: "Georgia, serif" }}>
              Lead Finder Italia
            </h1>
            <p className="text-sm text-stone-600">CRM lead centri estetici · Campagne email ODR</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={previewEmail} data-testid="btn-preview-email"><Eye className="w-4 h-4 mr-1" /> Anteprima email</Button>
            <Button onClick={() => setShowAdd(true)} className="bg-emerald-700 hover:bg-emerald-800" data-testid="btn-add-lead"><Plus className="w-4 h-4 mr-1" /> Nuovo Lead</Button>
          </div>
        </div>

        {/* Stats cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6" data-testid="stats-cards">
            <Card><CardContent className="p-4"><div className="flex items-center gap-3"><Users className="w-8 h-8 text-emerald-600" /><div><p className="text-xs text-stone-500 uppercase">Totale Lead</p><p className="text-2xl font-semibold">{stats.total}</p></div></div></CardContent></Card>
            <Card><CardContent className="p-4"><div className="flex items-center gap-3"><AlertCircle className="w-8 h-8 text-blue-600" /><div><p className="text-xs text-stone-500 uppercase">Nuovi</p><p className="text-2xl font-semibold">{stats.per_stato?.Nuovo || 0}</p></div></div></CardContent></Card>
            <Card><CardContent className="p-4"><div className="flex items-center gap-3"><Mail className="w-8 h-8 text-amber-600" /><div><p className="text-xs text-stone-500 uppercase">Email inviate</p><p className="text-2xl font-semibold">{stats.per_stato?.["Email Inviata"] || 0}</p></div></div></CardContent></Card>
            <Card><CardContent className="p-4"><div className="flex items-center gap-3"><CheckCircle className="w-8 h-8 text-purple-600" /><div><p className="text-xs text-stone-500 uppercase">Interessati</p><p className="text-2xl font-semibold">{stats.per_stato?.Interessato || 0}</p></div></div></CardContent></Card>
            <Card><CardContent className="p-4"><div className="flex items-center gap-3"><Clock className="w-8 h-8 text-red-600" /><div><p className="text-xs text-stone-500 uppercase">Invii rimasti oggi</p><p className="text-2xl font-semibold">{stats.invii_rimanenti_oggi} / {stats.limite_giornaliero}</p></div></div></CardContent></Card>
          </div>
        )}

        <Tabs defaultValue="contatti">
          <TabsList>
            <TabsTrigger value="contatti" data-testid="tab-contatti">📇 Contatti ({total})</TabsTrigger>
            <TabsTrigger value="import" data-testid="tab-import">📥 Importa CSV</TabsTrigger>
            <TabsTrigger value="campagne" data-testid="tab-campagne">📧 Invia Campagna</TabsTrigger>
          </TabsList>

          {/* CONTATTI */}
          <TabsContent value="contatti">
            <Card>
              <CardHeader>
                <div className="flex flex-wrap gap-2 items-center">
                  <div className="relative flex-1 min-w-[200px]">
                    <Search className="w-4 h-4 absolute left-3 top-3 text-stone-400" />
                    <Input placeholder="Comune..." value={filters.comune} onChange={(e) => setFilters({ ...filters, comune: e.target.value })} className="pl-9" data-testid="filter-comune" />
                  </div>
                  <Input placeholder="Regione..." value={filters.regione} onChange={(e) => setFilters({ ...filters, regione: e.target.value })} className="w-40" data-testid="filter-regione" />
                  <select value={filters.stato} onChange={(e) => setFilters({ ...filters, stato: e.target.value })} className="border rounded px-3 py-2 text-sm" data-testid="filter-stato">
                    <option value="">Tutti gli stati</option>
                    {STATES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                  <Button variant="outline" size="sm" onClick={loadLeads}><RefreshCw className="w-4 h-4" /></Button>
                </div>
              </CardHeader>
              <CardContent>
                {loading ? <p className="text-center py-8 text-stone-500">Caricamento…</p> :
                  leads.length === 0 ? <p className="text-center py-12 text-stone-500">Nessun lead trovato. Importa un CSV o aggiungi manualmente.</p> :
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="leads-table">
                      <thead className="bg-stone-50 text-xs uppercase text-stone-600">
                        <tr>
                          <th className="text-left p-3">Ragione Sociale</th>
                          <th className="text-left p-3">Email</th>
                          <th className="text-left p-3">Comune</th>
                          <th className="text-left p-3">Stato</th>
                          <th className="text-left p-3">Ultimo Invio</th>
                          <th className="text-right p-3">Azioni</th>
                        </tr>
                      </thead>
                      <tbody>
                        {leads.map(l => (
                          <tr key={l.id} className="border-t hover:bg-stone-50" data-testid={`lead-row-${l.id}`}>
                            <td className="p-3 font-medium">{l.ragione_sociale}</td>
                            <td className="p-3 text-stone-600">{l.email}</td>
                            <td className="p-3 text-stone-600">{l.comune || "—"}</td>
                            <td className="p-3"><Badge className={STATE_COLORS[l.stato] || "bg-gray-100"}>{l.stato}</Badge></td>
                            <td className="p-3 text-xs text-stone-500">{l.ultimo_invio ? new Date(l.ultimo_invio).toLocaleString("it-IT") : "—"}</td>
                            <td className="p-3 text-right">
                              <button onClick={() => sendBatch([l.id], false)} className="text-emerald-700 hover:text-emerald-900 text-xs mr-3" data-testid={`btn-send-${l.id}`}>Invia</button>
                              <button onClick={() => deleteLead(l.id)} className="text-red-600 hover:text-red-800"><Trash2 className="w-4 h-4" /></button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                }
              </CardContent>
            </Card>
          </TabsContent>

          {/* IMPORT CSV */}
          <TabsContent value="import">
            <Card>
              <CardHeader><CardTitle>Importa lista da CSV</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded p-4 text-sm text-blue-900">
                  <p className="font-semibold mb-2">📋 Formato CSV atteso (intestazioni):</p>
                  <code className="text-xs block bg-white p-2 rounded">ragione_sociale, email, telefono, sito_web, regione, provincia, comune, indirizzo, note</code>
                  <p className="text-xs mt-2">{'✅ Deduplica automatica sull\'email · Riconosce anche colonne alternative: "nome", "nome centro", "citta", "città", "website"'}</p>
                </div>
                <input type="file" accept=".csv" onChange={(e) => setCsvFile(e.target.files?.[0])} className="block text-sm" data-testid="csv-input" />
                {csvFile && <p className="text-sm text-emerald-700">File selezionato: {csvFile.name}</p>}
                <Button onClick={uploadCsv} disabled={!csvFile} className="bg-emerald-700 hover:bg-emerald-800" data-testid="btn-upload-csv"><Upload className="w-4 h-4 mr-1" /> Importa</Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* CAMPAGNE */}
          <TabsContent value="campagne">
            <Card>
              <CardHeader><CardTitle>Invia campagna email</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-amber-50 border border-amber-200 rounded p-4 text-sm">
                  <p className="font-semibold text-amber-900">🛡️ Regole di sicurezza</p>
                  <ul className="text-amber-800 text-xs mt-2 space-y-1 list-disc pl-5">
                    <li>Limite massimo <strong>80 email/giorno</strong> per non triggerare filtri antispam</li>
                    <li>Invii solo a lead con stato <strong>{'"Nuovo"'}</strong> (chi ha già ricevuto viene skippato)</li>
                    <li>Ogni invio aggiorna stato → <em>Email Inviata</em> con timestamp e ID Resend</li>
                    <li>Puoi rilanciare la campagna ogni giorno automaticamente sui nuovi lead</li>
                  </ul>
                </div>
                <div className="bg-stone-50 border rounded p-4">
                  <p className="text-sm font-semibold mb-2">📧 Anteprima subject della campagna</p>
                  <p className="text-sm italic bg-white p-2 rounded border">{'"Estate, dermatiti e irritazioni cutanee: una nuova opportunità per il tuo centro"'}</p>
                  <button onClick={previewEmail} className="text-xs text-emerald-700 hover:underline mt-2" data-testid="btn-preview-campaign">👁️ Vedi anteprima completa</button>
                </div>
                <div className="flex gap-3 flex-wrap">
                  <Button onClick={() => sendBatch(null, true)} disabled={sending} variant="outline" data-testid="btn-send-test"><Send className="w-4 h-4 mr-1" /> Simulazione TEST</Button>
                  <Button onClick={() => sendBatch(null, false)} disabled={sending} className="bg-emerald-700 hover:bg-emerald-800" data-testid="btn-send-real"><Send className="w-4 h-4 mr-1" /> {sending ? "Invio in corso…" : "Invia REALE (max 80)"}</Button>
                </div>
                {stats && <p className="text-xs text-stone-500">Invii oggi: <strong>{stats.limite_giornaliero - stats.invii_rimanenti_oggi}</strong> / {stats.limite_giornaliero} · Rimasti: <strong>{stats.invii_rimanenti_oggi}</strong></p>}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Dialog Add Lead */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent data-testid="dialog-add-lead">
          <DialogHeader><DialogTitle>Nuovo Lead Centro Estetico</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Ragione sociale (Centro Estetico Bella Vita) *" value={newLead.ragione_sociale} onChange={(e) => setNewLead({ ...newLead, ragione_sociale: e.target.value })} data-testid="input-nome" />
            <Input type="email" placeholder="Email *" value={newLead.email} onChange={(e) => setNewLead({ ...newLead, email: e.target.value })} data-testid="input-email" />
            <Input placeholder="Telefono" value={newLead.telefono} onChange={(e) => setNewLead({ ...newLead, telefono: e.target.value })} />
            <div className="grid grid-cols-2 gap-2">
              <Input placeholder="Comune" value={newLead.comune} onChange={(e) => setNewLead({ ...newLead, comune: e.target.value })} />
              <Input placeholder="Regione" value={newLead.regione} onChange={(e) => setNewLead({ ...newLead, regione: e.target.value })} />
            </div>
            <Textarea placeholder="Note" value={newLead.note} onChange={(e) => setNewLead({ ...newLead, note: e.target.value })} rows={2} />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowAdd(false)}>Annulla</Button>
              <Button onClick={createLead} className="bg-emerald-700 hover:bg-emerald-800" data-testid="btn-save-lead">Salva</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Preview Email */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="dialog-preview">
          <DialogHeader><DialogTitle>Anteprima email campagna</DialogTitle></DialogHeader>
          <div dangerouslySetInnerHTML={{ __html: previewHtml }} />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LeadFinderPage;
